import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Factory, Search, AlertCircle, Send } from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useAuth';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { ModalShell } from '../../../components/ui/ModalShell';
import { api, ApiError } from '../../../lib/api';
import { SapEmploymentCard } from '../person/sap/SapEmploymentCard';
import { PlanonCreateModal } from '../planon/PlanonCreateModal';
import { SystemCreationRow, type SystemPerson } from './SystemCreationRow';

type Bucket = 'blocked' | 'ready' | 'active' | 'awaiting_planon' | 'done';

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'blocked', label: 'Needs data' },
  { key: 'ready', label: 'Ready' },
  { key: 'active', label: 'Failed / running' },
  { key: 'awaiting_planon', label: 'Needs Planon' },
  { key: 'done', label: 'Fully created' },
];

/** Which working bucket a person falls into, for the status filters. */
function bucketOf(p: SystemPerson): Bucket {
  if (p.planon_status === 'done') return 'done';
  if (p.sap_status === 'failed' || p.sap_status === 'running') return 'active';
  if (p.sap_status === 'not_ready') return 'blocked';
  if (p.sap_status === 'ready') return 'ready';
  return 'awaiting_planon'; // in SAP, Planon still to do
}

/**
 * "People System Creation": every onboarding person and how far they
 * are through the downstream-system pipeline (data ready -> SAP ->
 * Planon). SAP must go first: it mints the employee id the other
 * systems key on. The Planon person push is a placeholder until its
 * API details exist.
 */
export function PeopleSystemCreation() {
  const { data: user, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const isAdmin = !!user?.roles.includes('admin');
  const [confirmFor, setConfirmFor] = useState<SystemPerson | null>(null);
  const [employmentFor, setEmploymentFor] = useState<SystemPerson | null>(null);
  const [planonFor, setPlanonFor] = useState<SystemPerson | null>(null);
  const [missingFor, setMissingFor] = useState<SystemPerson | null>(null);
  const [clearFor, setClearFor] = useState<SystemPerson | null>(null);
  const [search, setSearch] = useState('');
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // Fully-created people are hidden by default: nothing to do with them here.
  const [hidden, setHidden] = useState<Set<Bucket>>(() => new Set<Bucket>(['done']));

  const status = useQuery({
    queryKey: ['sap-people-status'],
    queryFn: () => api<{ people: SystemPerson[] }>('/api/admin/sap/people-status'),
    enabled: isAdmin,
    // Live-ish while any push is in flight; lazy otherwise.
    refetchInterval: (q) =>
      q.state.data?.people.some((p) => p.sap_status === 'running') ? 2500 : 15000,
  });

  // Dry run first, then push: the same safety order the person tab
  // enforces, automated so the dashboard button is one click.
  const push = useMutation({
    mutationFn: async (person: SystemPerson) => {
      const dry = await api<{ status: string; steps: { key: string; error?: string }[] }>(
        `/api/admin/sap/people/${person.id}/dry-run`,
        { method: 'POST' },
      );
      if (dry.status !== 'dry_run_complete') {
        const firstError = dry.steps.find((s) => s.error)?.error;
        throw new Error(`dry run failed${firstError ? `: ${firstError}` : ''}`);
      }
      return api<{ run_id: string }>(`/api/admin/sap/people/${person.id}/push`, {
        method: 'POST',
      });
    },
    onSuccess: (_r, person) => toast.success(`SAP push started for ${person.full_name}`),
    onError: (err, person) =>
      toast.error(
        `${person.full_name}: ${err instanceof ApiError || err instanceof Error ? err.message : 'push failed'}`,
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] }),
  });

  // Undo a dummy / imported employee code. The server refuses to clear a code
  // from a genuinely-hired or Planon-linked person, so this is import cleanup.
  const clearId = useMutation({
    mutationFn: (person: SystemPerson) =>
      api(`/api/admin/sap/people/${person.id}/clear-employee-id`, { method: 'POST' }),
    onSuccess: (_r, person) => {
      toast.success(`Cleared employee ID for ${person.full_name}`);
      void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] });
    },
    onError: (err, person) =>
      toast.error(
        `${person.full_name}: ${err instanceof ApiError ? err.message : 'could not clear employee ID'}`,
      ),
  });

  const people = status.data?.people ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      if (hidden.has(bucketOf(p))) return false;
      if (!q) return true;
      return (
        p.full_name.toLowerCase().includes(q) ||
        (p.position ?? '').toLowerCase().includes(q) ||
        (p.employee_id ?? '').toLowerCase().includes(q)
      );
    });
  }, [people, search, hidden]);
  const bucketCounts = useMemo(() => {
    const c: Record<Bucket, number> = {
      blocked: 0,
      ready: 0,
      active: 0,
      awaiting_planon: 0,
      done: 0,
    };
    for (const p of people) c[bucketOf(p)] += 1;
    return c;
  }, [people]);
  const toggleBucket = (b: Bucket) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  const counts = useMemo(
    () => ({
      done: people.filter((p) => p.sap_status === 'done').length,
      ready: people.filter((p) => p.sap_status === 'ready').length,
      blocked: people.filter((p) => p.sap_status === 'not_ready' || p.sap_status === 'failed')
        .length,
    }),
    [people],
  );
  const readyPeople = useMemo(() => people.filter((p) => p.sap_status === 'ready'), [people]);

  // One person through the same dry-run-then-push gate the single button uses.
  async function pushOneToSap(person: SystemPerson): Promise<boolean> {
    const dry = await api<{ status: string }>(`/api/admin/sap/people/${person.id}/dry-run`, {
      method: 'POST',
    });
    if (dry.status !== 'dry_run_complete') return false;
    await api(`/api/admin/sap/people/${person.id}/push`, { method: 'POST' });
    return true;
  }

  // Push every ready person, one at a time. The pushes queue and run
  // sequentially in the background; a dry-run failure skips only that person.
  async function pushAllReady() {
    const targets = readyPeople;
    setBulkProgress({ done: 0, total: targets.length });
    let started = 0;
    let skipped = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        if (await pushOneToSap(targets[i]!)) started += 1;
        else skipped += 1;
      } catch {
        skipped += 1;
      }
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
    void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] });
    if (skipped === 0) toast.success(`Started ${started} SAP push${started === 1 ? '' : 'es'}`);
    else toast.warning(`Started ${started}, skipped ${skipped} (not ready or dry run failed)`);
  }

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!isAdmin) {
    return (
      <main className="p-6 text-sm text-fg-muted">System creation is restricted to admin.</main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Factory className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">People</div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              People System Creation
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Track every onboarding person into the downstream systems. SAP goes first: it
              mints the employee ID the other systems use. {counts.done} in SAP, {counts.ready}{' '}
              ready to push, {counts.blocked} blocked.
            </p>
          </div>
        </header>

        {!status.isLoading && people.length > 0 ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, position or employee ID…"
                className="w-full rounded-token-sm border border-line/20 bg-surface py-2 pl-9 pr-3 text-sm outline-none focus:border-accent/40"
              />
            </div>
            {counts.ready > 0 ? (
              <Button
                variant="primary"
                size="md"
                iconLeft={<Send className="h-4 w-4" />}
                loading={bulkProgress !== null}
                onClick={() => setBulkConfirm(true)}
                className="whitespace-nowrap"
              >
                {bulkProgress
                  ? `Pushing ${bulkProgress.done}/${bulkProgress.total}…`
                  : `Push ${counts.ready} ready to SAP`}
              </Button>
            ) : null}
          </div>
        ) : null}

        {!status.isLoading && people.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-medium text-fg-dim">Show</span>
            {BUCKETS.map((b) => {
              const shown = !hidden.has(b.key);
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => toggleBucket(b.key)}
                  aria-pressed={shown}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                    shown
                      ? 'border-accent/30 bg-accent/10 text-accent'
                      : 'border-line/10 bg-surface-2 text-fg-dim hover:text-fg-muted'
                  }`}
                >
                  {b.label}
                  <span className="tabular-nums opacity-70">{bucketCounts[b.key]}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        {status.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <section className="rounded-lg border border-line/10 bg-surface shadow-sm">
            <div className="hidden items-center gap-3 border-b border-line/10 px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-muted sm:flex">
              <div className="min-w-0 flex-1">Person</div>
              <div className="w-52">Progress</div>
              <div className="w-36">Employee ID</div>
              <div className="w-80 text-right">Actions</div>
            </div>
            <div className="divide-y divide-divider/10">
              {people.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-fg-muted">
                  No onboarding people yet.
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-fg-muted">
                  No people match the current search or filters.
                </p>
              ) : (
                filtered.map((p) => (
                  <SystemCreationRow
                    key={p.id}
                    person={p}
                    pushing={push.isPending && push.variables?.id === p.id}
                    pushingPlanon={planonFor?.id === p.id}
                    onPush={() => setConfirmFor(p)}
                    onPushPlanon={() => setPlanonFor(p)}
                    onEditEmployment={() => setEmploymentFor(p)}
                    onShowMissing={() => setMissingFor(p)}
                    onClearEmployeeId={() => setClearFor(p)}
                  />
                ))
              )}
            </div>
          </section>
        )}

        <p className="text-xs text-fg-dim">
          Push to SAP runs a dry run first and only pushes when it passes. Push to Planon opens a
          create form: copy an existing CAFM person or fill it in manually.{' '}
          <Link to="/admin/sap" className="text-accent hover:underline">
            SAP integration settings
          </Link>
        </p>
      </div>

      {/* Same edit surface as the person SAP tab; readiness often only
          needs the employment record, so it is one click from the row. */}
      <ModalShell
        open={employmentFor !== null}
        onClose={() => {
          setEmploymentFor(null);
          void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] });
        }}
        titleId="employment-modal-title"
        maxWidth="max-w-2xl"
      >
        {employmentFor ? (
          <div className="p-1">
            <h2 id="employment-modal-title" className="sr-only">
              SAP employment record for {employmentFor.full_name}
            </h2>
            <p className="px-4 pt-3 text-sm font-medium">{employmentFor.full_name}</p>
            <div className="p-2">
              <SapEmploymentCard personId={employmentFor.id} />
            </div>
          </div>
        ) : null}
      </ModalShell>

      <ConfirmModal
        open={confirmFor !== null}
        title={confirmFor?.sap_status === 'failed' ? 'Resume SAP push' : 'Push to SAP'}
        message={
          confirmFor
            ? confirmFor.sap_status === 'failed'
              ? `This resumes the failed SAP push for ${confirmFor.full_name}. Steps that already ` +
                `succeeded (including the hire) are skipped, so it will not create a duplicate — it ` +
                `carries on from where it stopped. A dry run runs first. Continue?`
              : `This creates a NEW employee record for ${confirmFor.full_name} in SAP. ` +
                `A dry run is performed first and the push only proceeds when it passes. Continue?`
            : ''
        }
        confirmLabel={confirmFor?.sap_status === 'failed' ? 'Resume push' : 'Run dry run, then push'}
        tone="warning"
        onConfirm={() => {
          if (confirmFor) push.mutate(confirmFor);
          setConfirmFor(null);
        }}
        onClose={() => setConfirmFor(null)}
      />

      <ConfirmModal
        open={bulkConfirm}
        title={`Push ${readyPeople.length} ready to SAP`}
        message={
          `This pushes the ${readyPeople.length} ready ${readyPeople.length === 1 ? 'person' : 'people'} to SAP ` +
          `(${readyPeople
            .slice(0, 6)
            .map((p) => p.full_name)
            .join(', ')}${readyPeople.length > 6 ? `, and ${readyPeople.length - 6} more` : ''}), ` +
          `creating a NEW employee record for each. Every push runs a dry run first and only proceeds if it ` +
          `passes; they process one at a time in the background. Continue?`
        }
        confirmLabel={`Push ${readyPeople.length} to SAP`}
        tone="warning"
        onConfirm={() => {
          setBulkConfirm(false);
          void pushAllReady();
        }}
        onClose={() => setBulkConfirm(false)}
      />

      {planonFor ? (
        <PlanonCreateModal
          person={planonFor}
          onClose={() => setPlanonFor(null)}
          onCreated={() => {
            setPlanonFor(null);
            void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] });
          }}
        />
      ) : null}

      <ModalShell
        open={missingFor !== null}
        onClose={() => setMissingFor(null)}
        titleId="missing-modal-title"
        maxWidth="max-w-md"
      >
        {missingFor ? (
          <div className="p-5">
            <h2 id="missing-modal-title" className="text-sm font-semibold">
              What’s missing for {missingFor.full_name}
            </h2>
            <p className="mt-1 text-xs text-fg-dim">
              These are needed before this person can be pushed to SAP.
            </p>
            {missingFor.missing.length === 0 ? (
              <p className="mt-4 text-sm text-fg-muted">Nothing outstanding.</p>
            ) : (
              <ul className="mt-4 space-y-2">
                {missingFor.missing.map((m) => (
                  <li key={m} className="flex items-start gap-2 text-sm">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-warning-soft-fg" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </ModalShell>

      <ConfirmModal
        open={clearFor !== null}
        title="Clear employee ID?"
        tone="destructive"
        message={
          clearFor
            ? `Remove employee ID ${clearFor.employee_id} from ${clearFor.full_name}? Use this only for dummy or imported codes. People genuinely hired via SAP, or already linked to Planon, are protected and cannot be cleared here. Once cleared they show as not yet in SAP.`
            : ''
        }
        confirmLabel="Clear employee ID"
        onConfirm={() => {
          if (clearFor) clearId.mutate(clearFor);
          setClearFor(null);
        }}
        onClose={() => setClearFor(null)}
      />
    </main>
  );
}
